import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";

import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "../../shared.module";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { IPayment } from "src/app/services/model/get-trip-data.model";
import { HeaderProcessingService } from "src/app/services/header-processing.service";
import { ReportExportService } from "src/app/services/report-export.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { MatDialog } from "@angular/material/dialog";
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IInsertDertHeadersResultData, ResultCode } from "src/app/services/model/pos.api.model";
import { PopUpDialogInformalService, SharedService } from "@dert-services";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { TranslateService } from "@ngx-translate/core";
import { ReturnPaymentDialogComponent, ReturnPaymentDialogResult } from "../return-payment-dialog/return-payment-dialog.component";
import { Observable, concat, from, lastValueFrom, of } from "rxjs";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import { LoginService } from "src/app/services/login.service";
import { TransactionService } from "src/app/services/api/transaction.service";
import { getVoucherCode } from "src/app/functions/common-functions";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { SobitService } from "src/app/services/sobit.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AuthorizationPolicy,
  MANGER_ROLES,
  isAuthorizedForPolicy,
} from "src/app/guards/authorization-policy";

@Component({
  selector: "app-payments",
  templateUrl: "./payments.component.html",
  styleUrls: ["./payments.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, SharedModule, FontAwesomeModule],

})
export class PaymentsComponent implements OnInit {

  @Input() payments: IPayment[] = [];
  @Input() reservationId: string | null = null;
  @Input() disableReturn: boolean = false;
  @Input() formEmail: string | null = null;

  @Output() paymentReturned = new EventEmitter<void>();
  @Output() paymentsRefreshRequested = new EventEmitter<void>();

  private _paymentDescriptionCache = new Map<string, Observable<string>>();
  private _managerStornoPaymentNames = new Set<string>();
  private _sharedService = inject(SharedService);
  private readonly _sobitService = inject(SobitService);
  private readonly _destroyRef = inject(DestroyRef);

  public isPrintingReceipt = false;
  public isReturningPayment = false;

  PaymentStatusEnum = PaymentStatusEnum;
  getVoucherCode = getVoucherCode;
  TenderType = TenderType;
  readonly managerRoles = MANGER_ROLES;

  constructor(
    private cd: ChangeDetectorRef,
    private headerProcessingService: HeaderProcessingService,
    private reportExportService: ReportExportService,
    private dialog: MatDialog,
    private sharedApiService: SharedApiService,
    private transactionService: TransactionService,
    private popUpDialogInformalService: PopUpDialogInformalService,
    private translate: TranslateService,
    private loginService: LoginService,
  ) {}

  ngOnInit() {
    from(this._sharedService.getPaymentTypes())
      .pipe(
        catchError(error => {
          console.error('Failed to load payment types for cancellation', error);
          return of([]);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(paymentTypes => {
        this._managerStornoPaymentNames = new Set(
          (paymentTypes ?? [])
            .filter(paymentType => paymentType.ManagerStorno)
            .map(paymentType => paymentType.Name),
        );
        this.cd.markForCheck();
      });

    this._sobitService.paymentsRefreshRequested$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.paymentsRefreshRequested.emit());
  }

  public getStatusClass(status: PaymentStatusEnum, value: number | null = null): string {
    if (status === PaymentStatusEnum.WrongCredentials) {
      return value != null && value < 0
        ? 'aborted negative'
        : 'aborted';
    }

    if (value != null && value >= 0) {
      return PaymentStatusEnum[status]?.toLowerCase() || status?.toString() || '';
    }
    return (PaymentStatusEnum[status]?.toLowerCase() || status?.toString() || '') + ' negative';
  }

  public getPaymentDateTime(payment: IPayment): string | null {
    if (payment?.DataSource === "pos" || payment?.DataSource === "both") {
      return payment.PosPaidDate ?? payment.PaidDate;
    }

    return payment?.PaidDate ?? null;
  }

  public getPaymentDescription(paymentName: string): Observable<string> { 
    if (!paymentName || paymentName.trim().length === 0) {
      return of('');
    }

    // Return cached observable if available
    if (this._paymentDescriptionCache.has(paymentName)) {
      return this._paymentDescriptionCache.get(paymentName)!;
    }

    // Create observable from async operation
    const description$ = (async () => {
      const paymentTypes = await this._sharedService.getPaymentTypes();
      const paymentType = paymentTypes.find(pt => pt.Name === paymentName);
      return paymentType ? (paymentType.Description || paymentType.Name) : paymentName;
    })();

    // Convert promise to observable and cache it
    const observable = from(description$);
    this._paymentDescriptionCache.set(paymentName, observable);
    return observable;
  }

  public getReturnInfo(payment: IPayment): string | null {
    const reasonKey = this.getReturnDisabledReasonKey(payment);
    return reasonKey
      ? this.translate.instant(reasonKey)
      : this.translate.instant('webPos.CancelPaymentAction');
  }

  public canReturnPayment(payment: IPayment): boolean {
    return !this.getReturnDisabledReasonKey(payment);
  }

  private getReturnDisabledReasonKey(payment: IPayment): string | null {
    if (this.disableReturn) {
      return 'webPos.ReturnDisabledReasons.CannotReturn';
    }

    if (!payment || payment.Status !== PaymentStatusEnum.Done) {
      return payment?.Status === PaymentStatusEnum.Cancelled
        ? 'webPos.ReturnDisabledReasons.AlreadyReturned'
        : 'webPos.ReturnDisabledReasons.NotCompleted';
    }

    // Disable return for negative amounts (already returns)
    if (payment.Amount < 0) {
      return 'webPos.ReturnDisabledReasons.AlreadyReturned';
    }

    if (payment.DataSource === 'tiger') {
      return 'webPos.ReturnDisabledReasons.NotCashierPayment';
    }

    if (!payment.PaymentName || !this._managerStornoPaymentNames.has(payment.PaymentName)) {
      return 'webPos.ReturnDisabledReasons.UnsupportedPaymentType';
    }

    // Disable return if payment already has return metadata
    const dataBlob = payment.DataBlob as any;
    if (dataBlob && (dataBlob.returnMetadata || dataBlob.returnInfo)) {

      if (dataBlob.returnInfo) {
        let returnedByHeaderUID = dataBlob.returnInfo.ReturnedByHeaderUID;
        let returnedPayment = this.payments.find(p => p.HeaderUID === returnedByHeaderUID);
        if (returnedPayment) {
          if (returnedPayment.Status === PaymentStatusEnum.NotFound || returnedPayment.Status === PaymentStatusEnum.Cancelled) {
            return 'webPos.ReturnDisabledReasons.NotCompleted';
          }
        }
      }

      return 'webPos.ReturnDisabledReasons.AlreadyReturned';
    }

    if (!payment.BusinessUnitInternalNumber) {
      return 'webPos.ReturnDisabledReasons.DifferentRegister';
    } else if (payment.BusinessUnitInternalNumber !== this.loginService.user?.SelectedBusinessUnit) {
      return 'webPos.ReturnDisabledReasons.DifferentRegister';
    }

    // Check if payment date allows return
    // CR1 - payments can be cancelled only in the same month
    if (payment.PaidDate) {
      const now = new Date();
      const paymentDate = new Date(payment.PaidDate);
      
      const currentMonth = now.getMonth() + 1; // month is 0-indexed
      const currentYear = now.getFullYear();
      
      const paymentMonth = paymentDate.getMonth() + 1; // month is 0-indexed
      const paymentYear = paymentDate.getFullYear();
      
      // Payment in current month and year - always allowed
      if (paymentYear === currentYear && paymentMonth === currentMonth) {
        return null;
      }
           
      // Payment is too old
      return 'webPos.ReturnDisabledReasons.MonthlyLimitExpired';
    }

    return null;
  }

  public canPrintPayment(payment: IPayment): boolean {
    if (!payment) {
      return false;
    }

    // Must have HeaderID or HeaderUID
    if (!payment.HeaderID && !payment.HeaderUID) {
      return false;
    }

    // Cannot print if payment status is NotFound
    if (payment.Status !== PaymentStatusEnum.Done) {
      return false;
    }

    if (!payment.BusinessUnitInternalNumber) 
    {
      return false;
    }

    if (payment.BusinessUnitInternalNumber !== this.loginService.user?.SelectedBusinessUnit) 
    {
      return false;
    }

    return true;
  }

  public async onPrintReceipt(payment: IPayment, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isPrintingReceipt) {
      return;
    }

    // Active only when HeaderID is present in row data
    if (!payment?.HeaderID && !payment?.HeaderUID ) {
      return;
    }

    this.isPrintingReceipt = true;
    try {
      // If we also have HeaderUID, monitor header processing (like after payment)
      let headerIdForPrint = payment.HeaderID;
      const headerUid = payment.HeaderUID ?? null;

      if (!headerIdForPrint && headerUid) {
        const result = await this.headerProcessingService.monitorHeader(headerUid);
        const monitoredHeaderId = this.headerProcessingService.getHeaderIdFromResult(result);
        if (monitoredHeaderId) {
          headerIdForPrint = monitoredHeaderId;
        }
      }

      if (headerIdForPrint) {
        await this.reportExportService.downloadPdf(
          undefined,
          [{ key: 'HeaderID', value: headerIdForPrint }],
          true
        );
      }
    } catch (error) {
      console.error('Failed to print receipt from payments list', error);
    } finally {
      this.isPrintingReceipt = false;
    }
  }

  public async onReturnPayment(payment: IPayment, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (!isAuthorizedForPolicy(this.loginService.user?.Roles, AuthorizationPolicy.Cancellation)) {
      return;
    }

    if (this.isReturningPayment) {
      return;
    }

    if (!payment || !this.canReturnPayment(payment)) {
      return;
    }

    const dialogRef = this.dialog.open(ReturnPaymentDialogComponent, {
      disableClose: false,
      data: { payment },
    });

    const result = (await lastValueFrom(dialogRef.afterClosed())) as ReturnPaymentDialogResult | undefined;
    if (!result?.confirmed) {
      return;
    }

    const originalHeaderId = payment.HeaderID ?? null;
    const originalHeaderUid = payment.HeaderUID;

    if (!originalHeaderId && !originalHeaderUid) {
      await this.popUpDialogInformalService.confirm({
        title: this.translate.instant('webPos.Errors.Error'),
        message: this.translate.instant('webPos.Errors.MissingOriginalDocumentForReturn'),
        type: PopUpInformalType.Error,
      });
      return;
    }

    this.isReturningPayment = true;

    const request: IExecuteProcedureRequest<any> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.ReturnPayment,
      Parameters: {
        OriginalHeaderID: originalHeaderId,
        OriginalHeaderUID: originalHeaderUid,
        Note: result.note ?? null,
      },
    };

    const returnPayment$ = from(
      this.sharedApiService.executeProcedure(request) as Promise<IExecuteProcedureResult<any>>
    ).pipe(
      // Validate SP result
      tap(spResult => {
        if (!spResult || spResult.result !== ResultCode.OK) {
          throw new Error(this.translate.instant('webPos.Errors.ServerError'));
        }
      }),
      // Parse SP JSON output
      map(spResult => {
        const responsePayload: any = Array.isArray(spResult.data) ? spResult.data[0] : spResult.data;
        const jsonOutputCandidate = responsePayload?.JsonOutput ?? responsePayload?.jsonOutput ?? responsePayload;

        let parsedOutput: any = null;
        if (typeof jsonOutputCandidate === 'string') {
          try { parsedOutput = JSON.parse(jsonOutputCandidate); } catch { parsedOutput = null; }
        } else if (typeof jsonOutputCandidate === 'object' && jsonOutputCandidate) {
          parsedOutput = jsonOutputCandidate;
        }

        const spError: string | null = parsedOutput?.Error ?? parsedOutput?.error ?? null;
        if (spError) {
          throw new Error(this.translate.instant(spError));
        }

        return parsedOutput;
      }),
      // Extract HeaderUID and always monitor header to get HeaderID (same as standard payment flow)
      // Note: Never use HeaderID directly from response - always monitor via HeaderUID
      switchMap(parsedOutput => {
        const headerUid: string | null = parsedOutput?.HeaderUID ?? parsedOutput?.headerUid ?? null;

        // Always monitor header by HeaderUID (same as standard payment)
        if (headerUid) {
          return from(this.headerProcessingService.monitorHeader(headerUid)).pipe(
            map(monitorResult => this.headerProcessingService.getHeaderIdFromResult(monitorResult))
          );
        }

        return of(null as number | null);
      }),
      // Show success dialog and handle print
      switchMap(headerIdForPrint => {
        return from(this.popUpDialogInformalService.confirm({
          title: this.translate.instant('webPos.Done'),
          message: this.translate.instant('webPos.PaymentReturned'),
          type: PopUpInformalType.Success,
          showPrintReceipt: true,
          showSendReceiptByEmail: true,
          emailAddress: this.formEmail, // add from form
        })).pipe(
          tap(() => this.paymentReturned.emit()),
          switchMap(confirmPrint => {
            const print$ = (confirmPrint?.printReceipt && headerIdForPrint)
              ? from(this.reportExportService.downloadPdf(
                  undefined,
                  [{ key: 'HeaderID', value: headerIdForPrint }],
                  true
                )).pipe(
                  catchError(printError => {
                    console.error('Failed to print receipt after return', printError);
                    return from(this.popUpDialogInformalService.confirm({
                      title: this.translate.instant('webPos.Errors.Error'),
                      message: this.translate.instant('webPos.Errors.ServerError'),
                      type: PopUpInformalType.Error,
                    }));
                  })
                )
              : of(null);

            const email$ = (confirmPrint?.email && headerIdForPrint)
              ? from(this.reportExportService.sendPdfByEmail(confirmPrint.email, headerIdForPrint)).pipe(
                  catchError(emailError => {
                    console.error('Failed to send receipt email after return', emailError);
                    return of(null);
                  })
                )
              : of(null);

            return concat(print$, email$);
          })
        );
      }),
      catchError(error => {
        console.error('Failed to return payment', error);
        const errorMsg = error instanceof Error ? error.message : this.translate.instant('webPos.Errors.UnexpectedError');
        return from(this.popUpDialogInformalService.confirm({
          title: this.translate.instant('webPos.Errors.Error'),
          message: errorMsg,
          type: PopUpInformalType.Error,
        }));
      }),
      finalize(() => {
        this.isReturningPayment = false;
      })
    );

    await lastValueFrom(returnPayment$).catch(() => null);
  }

  
}
